import React, { useEffect, useState } from 'react';
import { X, Calendar, Download, Mail, Plus, Loader2 } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresa } from '../../contexts/EmpresaContext';

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
];

export default function ModalFechamento({ onFechar }) {
  const { usuario } = useAuth();
  const { empresaAtual } = useEmpresa() || {};
  const isCliente   = usuario?.perfil === 'cliente';
  const hoje        = new Date();

  const [mes,        setMes]        = useState(hoje.getMonth() + 1);
  const [ano,        setAno]        = useState(hoje.getFullYear());
  const [operacao,   setOperacao]   = useState('');
  // Ticket 907: cliente/contador com mais de uma empresa vinculada exportava
  // sempre o fechamento da primeira (empresaId ficava '' e o backend escolhia
  // sozinho) -- agora usa a empresa selecionada no seletor da sidebar.
  const [empresaId,  setEmpresaId]  = useState(empresaAtual?.id || '');
  const [empresas,   setEmpresas]   = useState([]);
  const [emails,     setEmails]     = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!isCliente) {
      api.get('/empresas').then(r => setEmpresas(r.data)).catch(() => {});
    }
  }, [isCliente]);

  useEffect(() => {
    if (isCliente && empresaAtual) setEmpresaId(empresaAtual.id);
  }, [isCliente, empresaAtual]);

  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onFechar]);

  function adicionarEmail() {
    const e = emailInput.trim().toLowerCase();
    if (!e || !/\S+@\S+\.\S+/.test(e)) return toast.error('E-mail inválido.');
    if (emails.includes(e)) { setEmailInput(''); return; }
    setEmails(prev => [...prev, e]);
    setEmailInput('');
  }

  function removerEmail(e) {
    setEmails(prev => prev.filter(x => x !== e));
  }

  async function executar(modo) {
    if (loading) return;
    if (modo === 'email' && !emails.length) return toast.error('Adicione pelo menos um e-mail.');

    const body = {
      mes, ano,
      ...(operacao  && { operacao }),
      ...(empresaId && { empresa_id: empresaId }),
      ...(modo === 'email' && { emails }),
    };

    setLoading(true);
    const t = toast.loading(modo === 'email' ? 'Enviando por e-mail...' : 'Gerando ZIP...');
    try {
      if (modo === 'email') {
        const { data } = await api.post('/documentos/fechamento', body);
        toast.success(data.mensagem, { id: t });
        onFechar();
      } else {
        const resp = await api.post('/documentos/fechamento', body, { responseType: 'blob' });
        const url  = URL.createObjectURL(resp.data);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `kairos_${MESES[mes - 1].toLowerCase()}_${ano}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Download iniciado!', { id: t });
        onFechar();
      }
    } catch (err) {
      const msg = err.response?.data?.erro || 'Erro ao exportar fechamento.';
      toast.error(msg, { id: t });
    } finally {
      setLoading(false);
    }
  }

  const anos = Array.from({ length: 5 }, (_, i) => hoje.getFullYear() - i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onFechar} />

      <div className="relative w-full max-w-md bg-[#0a0f1e] border border-surface-border rounded-2xl shadow-2xl">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-primary-400" />
            <span className="text-sm font-semibold text-slate-200">Fechamento do Mês</span>
          </div>
          <button onClick={onFechar}
            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-surface-card transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Formulário */}
        <div className="px-5 py-4 space-y-4">
          {/* Período */}
          <div>
            <label className="label">Período</label>
            <div className="grid grid-cols-2 gap-2">
              <select className="input text-sm" value={mes} onChange={e => setMes(Number(e.target.value))}>
                {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select className="input text-sm" value={ano} onChange={e => setAno(Number(e.target.value))}>
                {anos.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {/* Operação */}
          <div>
            <label className="label">Tipo de Documentos</label>
            <select className="input text-sm" value={operacao} onChange={e => setOperacao(e.target.value)}>
              <option value="">Compras e Vendas (todos)</option>
              <option value="recebimento">Somente Compras (recebimento)</option>
              <option value="emissao">Somente Vendas (emissão)</option>
            </select>
          </div>

          {/* Empresa — admin/contador */}
          {!isCliente && empresas.length > 0 && (
            <div>
              <label className="label">Empresa</label>
              <select className="input text-sm" value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
                <option value="">Todas as empresas</option>
                {empresas.map(e => (
                  <option key={e.id} value={e.id}>{e.razao_social}</option>
                ))}
              </select>
            </div>
          )}

          {/* E-mails */}
          <div>
            <label className="label">Enviar por e-mail <span className="text-slate-600 font-normal">(opcional — pressione Enter para adicionar)</span></label>
            <div className="flex gap-2">
              <input
                className="input text-sm flex-1"
                placeholder="destinatario@email.com"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); adicionarEmail(); }
                }}
              />
              <button onClick={adicionarEmail} className="btn-secondary px-3 py-2 text-sm flex-shrink-0">
                <Plus size={14} />
              </button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {emails.map(e => (
                  <span key={e}
                    className="flex items-center gap-1 text-xs bg-primary-900/40 text-primary-300 px-2 py-0.5 rounded-full">
                    {e}
                    <button onClick={() => removerEmail(e)}
                      className="text-primary-500 hover:text-red-400 transition-colors ml-0.5">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rodapé */}
        <div className="px-5 py-4 border-t border-surface-border flex gap-2 justify-end">
          <button onClick={onFechar} disabled={loading} className="btn-secondary text-sm py-1.5 px-3">
            Cancelar
          </button>
          {emails.length > 0 && (
            <button onClick={() => executar('email')} disabled={loading}
              className="btn-primary text-sm py-1.5 px-3">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Enviar ZIP
            </button>
          )}
          <button onClick={() => executar('download')} disabled={loading}
            className="btn-primary text-sm py-1.5 px-3">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Baixar ZIP
          </button>
        </div>
      </div>
    </div>
  );
}
